// frontend/src/hooks/useOrganizations.ts - NEW ORGANIZATION-SPECIFIC HOOKS
// Entity-specific hooks for organizations separated from core useApi.ts
// Insert this as a new file: useOrganizations.ts

import { useCallback, useMemo } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  OrganizationListResponse,
  OrganizationDetailResponse,
  OrganizationCreateResponse,
  
  // Organization types
  Organization,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
  OrganizationQueryParams,
  
  // Query parameter types
  QueryParams,
  
  // Batch operation types
  BatchOperationRequest,
  
  // Correspondence types
  Correspondence,
  CorrespondenceQueryParams
} from '@/api/types';

// ===== ORGANIZATION QUERY HOOKS =====

/**
 * Fetch paginated list of organizations with role-based access control
 */
export const useOrganizations = (params?: OrganizationQueryParams, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  
  // Use the original useApi hook - don't modify it
  const result = useApi(
    async (): Promise<PaginatedResponse<Organization>> => {
      
      // Role-based access control (existing logic)
      if (currentUser?.role !== 'implementor') {
        
        if (currentUser?.organization) {
          let userOrgId: string | undefined;
          
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
          
          if (userOrgId) {
            try {
              const singleOrgResponse = await apiClient.organizations.get(userOrgId);
              const organization: Organization = {
                ...(singleOrgResponse as any).organization,
                plan: (singleOrgResponse as any).organization.plan as 'basic' | 'standard' | 'enterprise' | undefined
              };
                            
              const singleOrgResult = {
                data: [organization],
                pagination: { currentPage: 1, totalPages: 1, totalItems: 1, itemsPerPage: 10, hasNextPage: false, hasPrevPage: false },
                total: 1, 
                totalPages: 1, 
                page: 1, 
                limit: 10
              };
              
              return singleOrgResult;
            } catch (error: any) {
              console.error('useOrganizations: Error fetching single org:', error);
              const emptyResult = { 
                data: [], 
                pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10, hasNextPage: false, hasPrevPage: false }, 
                total: 0, 
                totalPages: 0, 
                page: 1, 
                limit: 10 
              };
              return emptyResult;
            }
          }
        }
        
        const emptyResult = { 
          data: [], 
          pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10, hasNextPage: false, hasPrevPage: false }, 
          total: 0, 
          totalPages: 0, 
          page: 1, 
          limit: 10 
        };
        return emptyResult;
      }
      
      // Implementors - make API call
      const response = await apiClient.organizations.list(params);

      // FIXED: Backend now returns correct format { data: [], pagination: {} }
      // No transformation needed - use normalization directly
      return normalizePaginatedResponse<Organization>(response);
    },
    {
      immediate: true,
      dependencies: useMemo(() => [
        JSON.stringify(params || {}),
        currentUser?.role,
        typeof currentUser?.organization === 'string' ? currentUser.organization : (currentUser?.organization as any)?._id
      ], [params, currentUser?.role, currentUser?.organization]),
      rateLimitKey: `organizations_list_${currentUser?.role}`,
      ...options
    }
  );


  return result;
};

/**
 * Fetch single organization by ID with access validation
 */
export const useOrganization = (id?: string, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<Organization> => {
      validateObjectId(id, 'Organization ID');
      
      
      // Security validation - only implementors can access any organization
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && id !== userOrgId) {
          console.warn('useOrganization: Security - User attempting to access different organization');
          throw new Error('Access denied: You can only view your own organization');
        }
      }
      
      const response = await apiClient.organizations.get(id!);
      
      // Type assertion to handle the plan field type mismatch
      const organization: Organization = {
        ...(response as any).organization,
        plan: (response as any).organization.plan as 'basic' | 'standard' | 'enterprise' | undefined
      };
      
      return organization;
    },
    {
      immediate: !!id,
      rateLimitKey: `organization_${id}`,
      ...options
    }
  );
};

/**
 * Fetch organization users
 */
export const useOrganizationUsers = (organizationId?: string, params?: any, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<any>> => {
      if (!organizationId) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      // Security validation - only implementors can query across organizations
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && organizationId !== userOrgId) {
          console.warn('useOrganizationUsers: Security - User attempting to access different organization users');
          throw new Error('Access denied: You can only view users from your own organization');
        }
      }
      
      
      // Check if getUsers method exists on organizations
      if (apiClient.organizations && 'getUsers' in apiClient.organizations) {
        const response = await (apiClient.organizations as any).getUsers(organizationId, params);
        return normalizePaginatedResponse(response);
      } else {
        // Fallback to general users list with organizationId filter
        const response = await apiClient.users.list({ organizationId, ...params });
        return normalizePaginatedResponse(response);
      }
    },
    {
      immediate: !!organizationId,
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `organization_users_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch organization correspondences
 */
export const useOrganizationCorrespondences = (organizationId?: string, params?: CorrespondenceQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Correspondence>> => {
      if (!organizationId) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if getCorrespondences method exists on organizations
      if (apiClient.organizations && 'getCorrespondences' in apiClient.organizations) {
        const response = await (apiClient.organizations as any).getCorrespondences(organizationId, params);
        return normalizePaginatedResponse<Correspondence>(response);
      } else if (apiClient.correspondences && apiClient.correspondences.list) {
        // Fallback to general correspondences list with params
        const response = await apiClient.correspondences.list(params);
        return normalizePaginatedResponse<Correspondence>(response);
      } else {
        // Return empty result if no endpoint is available
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
    },
    {
      immediate: !!organizationId,
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `organization_correspondences_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch organization analytics
 */
export const useOrganizationAnalytics = (organizationId?: string, params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if getAnalytics method exists
      if (!apiClient.organizations?.getAnalytics) {
        throw new Error('Organization analytics endpoint not available');
      }
      
      const response = await apiClient.organizations.getAnalytics(organizationId!, params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for analytics
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `organization_analytics_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch organization metrics
 */
export const useOrganizationMetrics = (organizationId?: string, params?: QueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if getMetrics method exists
      if (!apiClient.organizations?.getMetrics) {
        throw new Error('Organization metrics endpoint not available');
      }
      
      const response = await apiClient.organizations.getMetrics(organizationId!, params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for metrics
      dependencies: [organizationId, JSON.stringify(params || {})],
      rateLimitKey: `organization_metrics_${organizationId}`,
      ...options
    }
  );
};

/**
 * Fetch organization settings
 */
export const useOrganizationSettings = (organizationId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if getSettings method exists
      if (!apiClient.organizations?.getSettings) {
        throw new Error('Organization settings endpoint not available');
      }
      
      const response = await apiClient.organizations.getSettings(organizationId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!organizationId,
      rateLimitKey: `organization_settings_${organizationId}`,
      ...options
    }
  );
};

// ===== ORGANIZATION MUTATION HOOKS =====

// Fixed useCreateOrganization Hook - Replace in useOrganizations.ts
// This replaces the existing useCreateOrganization function

/**
 * Create new organization with comprehensive validation and response handling
 */
export const useCreateOrganization = (options?: UseMutationOptions<Organization, OrganizationCreateRequest>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation<Organization, OrganizationCreateRequest>(
    async (organizationData: OrganizationCreateRequest): Promise<Organization> => {
      
      // Role validation - only implementors can create organizations
      if (currentUser?.role !== 'implementor') {
        console.error('useCreateOrganization: Security - Non-implementor attempting to create organization');
        throw new Error('Access denied: Only implementors can create organizations');
      }
      
      // Validate required fields
      if (!organizationData.name?.trim()) {
        throw new Error('Organization name is required');
      }
      
      if (!organizationData.adminUser?.username || !organizationData.adminUser?.email) {
        throw new Error('Admin user information is required for new organizations');
      }
      
      // Additional validation
      if (organizationData.name.length < 2) {
        throw new Error('Organization name must be at least 2 characters long');
      }
      
      if (organizationData.name.length > 100) {
        throw new Error('Organization name must be less than 100 characters');
      }
      
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(organizationData.adminUser.email)) {
        throw new Error('Please enter a valid admin email address');
      }
      
      // Username format validation
      const usernameRegex = /^[a-zA-Z0-9._-]{3,30}$/;
      if (!usernameRegex.test(organizationData.adminUser.username)) {
        throw new Error('Admin username must be 3-30 characters and contain only letters, numbers, periods, underscores, and hyphens');
      }
      
      try {
        // Call the API using the correct method signature
        const response = await apiClient.organizations.create(organizationData);
        
        // Handle the API response based on actual response structure
        // The response is OrganizationCreateResponse: { success: true, message: string, data: { organization: Organization, ... } }
        let organization: any;
        
        if (response.data && response.data.organization) {
          // Standard response structure from backend
          organization = response.data.organization;
        } else if ((response as any).organization) {
          // Direct organization object (fallback)
          organization = (response as any).organization;
        } else {
          // Last resort fallback
          organization = response as any;
        }
        
        // Ensure organization has required fields
        if (!organization || !organization.name) {
          console.error('useCreateOrganization: Invalid organization data received:', organization);
          throw new Error('Invalid organization data received from server');
        }
        
        // Normalize organization to match the exact Organization interface
        const normalizedOrganization: Organization = {
          // Core identification
          id: organization.id || organization._id || '',
          _id: organization._id || organization.id,
          name: organization.name,
          description: organization.description,
          
          // Resource limits
          maxUsers: organization.maxUsers,
          maxProjects: organization.maxProjects,
          maxGroups: organization.maxGroups,
          maxWorkflows: organization.maxWorkflows,
          
          // Email management settings
          maxEmailAccounts: organization.maxEmailAccounts,
          emailRetentionDays: organization.emailRetentionDays,
          maxEmailStorage: organization.maxEmailStorage,
          maxCorrespondenceStorage: organization.maxCorrespondenceStorage,
          maxTotalStorage: organization.maxTotalStorage,
          emergencyPreservationEnabled: organization.emergencyPreservationEnabled,
          
          // Storage and correspondence limits
          maxCorrespondences: organization.maxCorrespondences,
          maxStorage: organization.maxStorage, // Legacy field
          
          // Plan and features - ensure proper typing
          plan: organization.plan as 'basic' | 'standard' | 'enterprise' | undefined,
          isAIEnabled: organization.isAIEnabled,
          subscribedModules: organization.subscribedModules || [],
          isActive: organization.isActive,
          
          // Status fields
          deactivated: organization.deactivated,
          
          // Backend field names (from Organization.js model)
          numUsers: organization.numUsers || 0,
          numProjects: organization.numProjects || 0,
          numGroups: organization.numGroups || 0,
          numWorkflows: organization.numWorkflows || 0,
          numEmailAccounts: organization.numEmailAccounts || 0,
          numEmails: organization.numEmails || 0,
          numCorrespondences: organization.numCorrespondences || 0,
          
          // Frontend aliases for backward compatibility
          userCount: organization.userCount || organization.numUsers || 0,
          projectCount: organization.projectCount || organization.numProjects || 0,
          emailCount: organization.emailCount || organization.numEmails || 0,
          correspondenceCount: organization.correspondenceCount || organization.numCorrespondences || 0,
          
          // Timestamps
          createdAt: organization.createdAt || new Date().toISOString(),
          updatedAt: organization.updatedAt,
          
          // Storage metrics - ensure they match Organization interface
          storageMetrics: organization.storageMetrics || {
            totalEmailStorage: 0,
            totalCorrespondenceStorage: 0,
            totalStorageUsage: 0
          },
          
          // Email metrics - ensure they match Organization interface
          emailMetrics: organization.emailMetrics || {
            totalEmails: 0,
            preservedEmails: 0,
            temporaryEmails: 0,
            expiredEmails: 0,
            emailPreservationRate: 0,
            averageEmailRetentionDays: 0
          },
          
          // Correspondence metrics - FIXED to match actual Organization interface
          correspondenceMetrics: organization.correspondenceMetrics || {
            totalCorrespondences: 0,
            projectCorrespondences: 0,
            personalCorrespondences: 0,
            manualCorrespondences: 0, // This field is required in Organization interface
            autoClassifiedCorrespondences: 0,
            correspondencesWithAttachments: 0,
            manuallyReviewedCorrespondences: 0,
            emailToCorrespondenceConversionRate: 0,
            averageClassificationScore: 0
          },
        };
        
        
        return normalizedOrganization;
        
      } catch (error: any) {
        console.error('useCreateOrganization: API call failed:', error);
        
        // Extract meaningful error message
        let errorMessage = 'Failed to create organization';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Handle specific error cases
        if (error.response?.status === 409) {
          errorMessage = 'Organization name or admin username already exists';
        } else if (error.response?.status === 403) {
          errorMessage = 'Access denied: Insufficient permissions to create organization';
        } else if (error.response?.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid organization data provided';
        } else if (error.response?.status === 422) {
          // Validation errors
          if (error.response.data?.errors) {
            const validationErrors = error.response.data.errors;
            if (Array.isArray(validationErrors)) {
              errorMessage = validationErrors.map((err: any) => err.message || err).join(', ');
            } else {
              errorMessage = JSON.stringify(validationErrors);
            }
          }
        }
        
        console.error('useCreateOrganization: Final error message:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    options
  );
};

/**
 * Update existing organization
 */
export const useUpdateOrganization = (options?: UseMutationOptions<Organization, { id: string; data: OrganizationUpdateRequest }>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async ({ id, data }: { id: string; data: OrganizationUpdateRequest }): Promise<Organization> => {
      validateObjectId(id, 'Organization ID');
      
      // Security validation - only implementors can update any organization
      if (currentUser?.role !== 'implementor') {
        let userOrgId: string | undefined;
        
        if (currentUser?.organization) {
          if (typeof currentUser.organization === 'string') {
            userOrgId = currentUser.organization;
          } else if ((currentUser.organization as any)._id) {
            userOrgId = (currentUser.organization as any)._id;
          }
        }
        
        if (userOrgId && id !== userOrgId) {
          throw new Error('Access denied: You can only update your own organization');
        }
      }
      
      // Data validation
      if (data.name !== undefined) {
        if (!data.name?.trim()) {
          throw new Error('Organization name is required');
        }
        
        if (data.name.length < 2) {
          throw new Error('Organization name must be at least 2 characters long');
        }
        
        if (data.name.length > 100) {
          throw new Error('Organization name must be less than 100 characters');
        }
      }
      
      
      const response = await apiClient.organizations.update(id, data);
      
      return normalizeSingleResponse<Organization>(response);
    },
    options
  );
};

/**
 * Delete organization (admin and implementor)
 */
export const useDeleteOrganization = (options?: UseMutationOptions<void, string>) => {
  const { user: currentUser } = useAuthStore();

  return useMutation(
    async (organizationId: string): Promise<void> => {
      // Role validation - allow both admin and implementor
      if (!['admin', 'implementor'].includes(currentUser?.role || '')) {
        throw new Error('Only admins and implementors can delete organizations');
      }

      validateObjectId(organizationId, 'Organization ID');


      const response = await apiClient.organizations.delete(organizationId);

      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

/**
 * Activate organization
 */
export const useActivateOrganization = (options?: UseMutationOptions<Organization, string>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async (organizationId: string): Promise<Organization> => {
      // Role validation
      if (currentUser?.role !== 'implementor') {
        throw new Error('Only implementors can activate organizations');
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if activate method exists
      if (!apiClient.organizations || !('activate' in apiClient.organizations)) {
        throw new Error('Organization activation not available in current API version');
      }
      
      const response = await (apiClient.organizations as any).activate(organizationId);
      
      return normalizeSingleResponse<Organization>(response);
    },
    options
  );
};

/**
 * Deactivate organization
 */
export const useDeactivateOrganization = (options?: UseMutationOptions<Organization, string>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async (organizationId: string): Promise<Organization> => {
      // Role validation
      if (currentUser?.role !== 'implementor') {
        throw new Error('Only implementors can deactivate organizations');
      }
      
      validateObjectId(organizationId, 'Organization ID');
      
      
      // Check if deactivate method exists
      if (!apiClient.organizations || !('deactivate' in apiClient.organizations)) {
        throw new Error('Organization deactivation not available in current API version');
      }
      
      const response = await (apiClient.organizations as any).deactivate(organizationId);
      
      return normalizeSingleResponse<Organization>(response);
    },
    options
  );
};

/**
 * Update organization settings
 */
export const useUpdateOrganizationSettings = (options?: UseMutationOptions<any, { organizationId: string; settings: any }>) => {
  return useMutation(
    async ({ organizationId, settings }: { organizationId: string; settings: any }): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      
      if (!settings || typeof settings !== 'object') {
        throw new Error('Settings data is required');
      }
      
      
      // Check if updateSettings method exists
      if (!apiClient.organizations?.updateSettings) {
        throw new Error('Organization settings update not available in current API version');
      }
      
      const response = await apiClient.organizations.updateSettings(organizationId, settings);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Batch organization operations (implementor only)
 */
export const useBatchOrganizationOperations = (options?: UseMutationOptions<any, { operation: 'activate' | 'deactivate' | 'delete'; organizationIds: string[] }>) => {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async ({ operation, organizationIds }: { operation: 'activate' | 'deactivate' | 'delete'; organizationIds: string[] }): Promise<any> => {
      // Role validation
      if (currentUser?.role !== 'implementor') {
        throw new Error('Only implementors can perform batch organization operations');
      }
      
      if (!organizationIds || organizationIds.length === 0) {
        throw new Error('At least one organization ID is required');
      }
      
      if (!['activate', 'deactivate', 'delete'].includes(operation)) {
        throw new Error('Invalid operation. Must be activate, deactivate, or delete');
      }
      
      validateObjectIds(organizationIds, 'Organization IDs');

      // Check if bulk operations exist
      if (apiClient.organizations && 'bulkOperation' in apiClient.organizations) {
        const response = await (apiClient.organizations as any).bulkOperation({
          organizationIds,
          operation
        });
        return normalizeSingleResponse(response);
      } else {
        // Fallback to individual operations
        const results = [];
        
        for (const orgId of organizationIds) {
          try {
            let result;
            switch (operation) {
              case 'activate':
                if (apiClient.organizations && 'activate' in apiClient.organizations) {
                  result = await (apiClient.organizations as any).activate(orgId);
                } else {
                  throw new Error('Organization activation not available');
                }
                break;
              case 'deactivate':
                if (apiClient.organizations && 'deactivate' in apiClient.organizations) {
                  result = await (apiClient.organizations as any).deactivate(orgId);
                } else {
                  throw new Error('Organization deactivation not available');
                }
                break;
              case 'delete':
                result = await apiClient.organizations.delete(orgId);
                break;
            }
            results.push({ organizationId: orgId, success: true, result });
          } catch (error: any) {
            results.push({ organizationId: orgId, success: false, error: error.message });
          }
        }
        
        return {
          results,
          totalProcessed: organizationIds.length,
          successful: results.filter(r => r.success).length,
          failed: results.filter(r => !r.success).length
        };
      }
    },
    options
  );
};

/**
 * Search organizations with advanced filtering
 */
export const useSearchOrganizations = (query?: string, filters?: any, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<Organization>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      // Role-based access control
      if (currentUser?.role !== 'implementor') {
        console.warn('useSearchOrganizations: Non-implementor user attempting to search organizations');
        throw new Error('Access denied: Only implementors can search across organizations');
      }
      
      
      // Check if search endpoint exists, otherwise fall back to list with search params
      if (apiClient.organizations && 'search' in apiClient.organizations) {
        const response = await (apiClient.organizations as any).search(query, filters);
        return normalizePaginatedResponse<Organization>(response);
      } else if (apiClient.organizations && 'list' in apiClient.organizations) {
        // Fallback to list with search parameter - cast to any to handle type inference
        const searchParams: OrganizationQueryParams = {
          search: query,
          ...filters
        };
        
        const response = await (apiClient.organizations as any).list(searchParams);
        return normalizePaginatedResponse<Organization>(response);
      } else {
        // No organizations API available
        throw new Error('Organizations API not available');
      }
    },
    {
      immediate: !!query?.trim() && currentUser?.role === 'implementor',
      dependencies: [query, JSON.stringify(filters || {}), currentUser?.role],
      debounce: 300,
      retryCount: 2,
      rateLimitKey: 'search_organizations',
      ...options
    }
  );
};

/**
 * Organization analytics and reporting (implementor only)
 */
export const useOrganizationsAnalytics = (params?: any, options?: UseApiOptions) => {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async () => {
      // Role validation
      if (currentUser?.role !== 'implementor') {
        throw new Error('Access denied: Only implementors can view organization analytics');
      }
      
      
      // Check if analytics endpoint exists
      if (!apiClient.organizations || !('getAnalytics' in apiClient.organizations)) {
        throw new Error('Organization analytics endpoint not available');
      }
      
      const response = await (apiClient.organizations as any).getAnalytics(params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for analytics
      dependencies: [JSON.stringify(params || {}), currentUser?.role],
      retryCount: 1,
      rateLimitKey: 'organizations_analytics',
      ...options
    }
  );
};

// ===== EXPORTS =====

// Export all organization-related hooks
export {
  // Query hooks
  // useOrganizations, useOrganization - already exported above
  // useOrganizationUsers, useOrganizationCorrespondences - already exported above
  // useOrganizationAnalytics, useOrganizationMetrics, useOrganizationSettings - already exported above
  
  // Mutation hooks  
  // useCreateOrganization, useUpdateOrganization, useDeleteOrganization - already exported above
  // useActivateOrganization, useDeactivateOrganization - already exported above
  // useUpdateOrganizationSettings, useBatchOrganizationOperations - already exported above
  
  // Search and analytics
  // useSearchOrganizations, useOrganizationsAnalytics - already exported above
};

// Default export for convenience
const organizationHooks = {
  // Query hooks
  useOrganizations,
  useOrganization,
  useOrganizationUsers,
  useOrganizationCorrespondences,
  useOrganizationAnalytics,
  useOrganizationMetrics,
  useOrganizationSettings,
  
  // Mutation hooks
  useCreateOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
  useActivateOrganization,
  useDeactivateOrganization,
  useUpdateOrganizationSettings,
  useBatchOrganizationOperations,
  
  // Search and analytics
  useSearchOrganizations,
  useOrganizationsAnalytics
};

export default organizationHooks;